import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import arClient from '../../util/api_client';
import { OK } from '../../constants/platformStatusCodes';

const initialState = {
  ecommerceAvatars: [],
  productCategories: [],
  influencerCategories: [],
};

const ECommerceSlice = createSlice({
  name: 'Ecommerce',
  initialState,
  reducers: {
    setECommerceAvatars(state, action) {
      const { payload } = action;
      state.ecommerceAvatars = payload;
    },
    setProductCategories(state, action) {
      const { payload } = action;
      state.productCategories = payload;
    },
    setInfluencerCategories(state, action) {
      const { payload } = action;
      state.influencerCategories = payload;
    },
    addEcommerceAvatars(state, action) {
      const { payload } = action;
      state.ecommerceAvatars.push(...payload);
    },
  },
});

export const {
  setECommerceAvatars,
  setProductCategories,
  setInfluencerCategories,
  addEcommerceAvatars,
} = ECommerceSlice.actions;

export const getEcommerceCategories = (filter) => (dispatch) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.isCancelled) {
        return;
      }
      if (resp.status === 'OK' || resp.status === OK || resp.status === 'INVALID_DATA') {
        try {
          if (filter === 'influencer') {
            dispatch(setInfluencerCategories(resp.data));
          } else if (filter === 'product') {
            dispatch(setProductCategories(resp.data));
          }
          resolve(resp);
        } catch (e) {
          console.error('error loading categories: ', e);
        }
      } else {
        reject(new Error(resp?.error || 'Failed to get categories'));
      }
    };

    arClient.doApiCall('/e-commerce/getCategories', { genre: filter }, callback);
  });
};

export const getCoverUrls = (urlJSON) => (dispatch) => {
  const URLs = urlJSON
    .map((item) => {
      if (_.isObject(item)) {
        return item.url;
      }
      return item;
    })
    .filter((url) => url && typeof url === 'string' && url.trim().length > 0);

  if (URLs.length === 0) {
    return Promise.resolve({ status: 'OK', data: [] });
  }

  const chunkArray = (array, chunkSize) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };

  if (URLs.length <= 10) {
    return new Promise((resolve, reject) => {
      const callback = (resp) => {
        if (resp.status === 'OK' || resp.status === OK) {
          try {
            dispatch(addEcommerceAvatars(resp.data));
            resolve(resp);
          } catch (e) {
            console.error('Error loading avatars: ', e);
            reject(e);
          }
        } else {
          reject(new Error(resp?.error || 'Failed to get cover URLs'));
        }
      };

      arClient.doApiCall('/e-commerce/getCoverUrls', { urlJSON: JSON.stringify(URLs) }, callback);
    });
  }

  const urlChunks = chunkArray(URLs, 10);

  return new Promise((resolve, reject) => {
    const apiCalls = urlChunks.map((chunk) => {
      return new Promise((chunkResolve, chunkReject) => {
        const callback = (resp) => {
          if (resp.status === 'OK' || resp.status === OK) {
            try {
              dispatch(addEcommerceAvatars(resp.data));
              chunkResolve(resp);
            } catch (e) {
              console.error('Error loading avatars: ', e);
              chunkReject(e);
            }
          } else {
            chunkReject(new Error(resp?.error || 'Failed to get cover URLs chunk'));
          }
        };

        arClient.doApiCall(
          '/e-commerce/getCoverUrls',
          { urlJSON: JSON.stringify(chunk) },
          callback
        );
      });
    });

    Promise.all(apiCalls)
      .then((responses) => {
        const combinedData = responses.reduce((acc, resp) => {
          if (resp.data && Array.isArray(resp.data)) {
            acc.push(...resp.data);
          }
          return acc;
        }, []);

        resolve({
          status: 10,
          data: combinedData,
        });
      })
      .catch((error) => {
        console.error('Error in one or more API calls:', error);
        reject(error);
      });
  });
};

// eslint-disable-next-line no-unused-vars
export const getEcommerceDetail = (genre, id) => (dispatch) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === 'OK' || resp.status === OK || resp.status === 'INVALID_DATA') {
        try {
          resolve(resp);
        } catch (e) {
          console.error('error loading details: ', e);
        }
      } else {
        reject(new Error(resp?.error || 'Failed to get ecommerce detail'));
      }
    };

    arClient.doApiCall('/e-commerce/detail', { genre, id }, callback);
  });
};

// eslint-disable-next-line no-unused-vars
export const getEcommerceDetailList = (genre, data) => (dispatch) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === 'OK' || resp.status === OK || resp.status === 'INVALID_DATA') {
        try {
          resolve(resp);
        } catch (e) {
          console.error('error loading details: ', e);
        }
      } else {
        reject(new Error(resp?.error || 'Failed to get ecommerce detail list'));
      }
    };

    arClient.doApiCall('/e-commerce/list', { genre, searchParams: JSON.stringify(data) }, callback);
  });
};

export function fetchECommerceList(genre, searchParams) {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === 'OK' || resp.status === OK || resp.status === 'INVALID_DATA') {
        try {
          resolve(resp);
        } catch (e) {
          console.error('error loading list: ', e);
        }
      } else {
        reject(new Error(resp?.error || 'Failed to fetch ecommerce list'));
      }
    };

    arClient.doApiCall(
      '/e-commerce/list',
      { genre, searchParams: JSON.stringify(searchParams) },
      callback
    );
  });
}

export const fetchECommerceDetail = (genre, id) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === 'OK' || resp.status === OK || resp.status === 'INVALID_DATA') {
        try {
          resolve(resp);
        } catch (e) {
          console.error('error loading details: ', e);
        }
      } else {
        reject(new Error(resp?.error || 'Failed to fetch ecommerce detail'));
      }
    };

    arClient.doApiCall('/e-commerce/detail', { genre, id }, callback);
  });
};

export default ECommerceSlice.reducer;
